import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Row = {
  id: string;
  slug: string;
  orderIndex: number;
  titleLight?: string | null;
  titleAccent?: string | null;
  description?: string | null;
  mapSrc?: string | null;
  isActive: boolean;
  updatedAt?: string;
};

const VisitItemsYonetim: React.FC = () => {

  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [toast, setToast] = React.useState<{ show: boolean; text: string }>({ show: false, text: '' });

  const [selectedId, setSelectedId] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [orderIndex, setOrderIndex] = React.useState<number>(0);
  const [titleLight, setTitleLight] = React.useState('');
  const [titleAccent, setTitleAccent] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [mapSrc, setMapSrc] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const fetchAll = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl('/api/visit-items/admin'));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data: Row[] = await res.json();
      setItems(data || []);
      const initial = (data && data[0]) || null;
      if (initial) {
        setSelectedId(initial.id);
        setSlug(initial.slug || '');
        setOrderIndex(initial.orderIndex || 0);
        setTitleLight(initial.titleLight || '');
        setTitleAccent(initial.titleAccent || '');
        setDescription(initial.description || '');
        setMapSrc(initial.mapSrc || '');
        setIsActive(!!initial.isActive);
      } else {
        setSelectedId('');
      }
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSelect = (id: string) => {
    setSelectedId(id);
    const it = items.find(x => x.id === id);
    if (!it) return;
    setSlug(it.slug || '');
    setOrderIndex(it.orderIndex || 0);
    setTitleLight(it.titleLight || '');
    setTitleAccent(it.titleAccent || '');
    setDescription(it.description || '');
    setMapSrc(it.mapSrc || '');
    setIsActive(!!it.isActive);
  };

  const onSave = async () => {
    if (!selectedId) { setError('Düzenlenecek kayıt yok'); return; }
    if (!slug.trim()) { setError('Lütfen slug girin'); return; }
    try {
      setSaving(true); setError('');
      const body: any = {
        slug: slug.trim(),
        orderIndex: orderIndex || 0,
        titleLight: titleLight || null,
        titleAccent: titleAccent || null,
        description: description || null,
        mapSrc: mapSrc || null,
        isActive: !!isActive,
      };
      const res = await fetch(toUrl(`/api/visit-items/admin/${selectedId}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      await fetchAll();
      setToast({ show: true, text: 'Başarılı' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Ziyaret Öğeleri (Visit Items)</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Kayıt: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Alanlar</div>
          <button onClick={fetchAll} className="btn">Yenile</button>
        </div>

        {items.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Kayıt Seç</label>
            <select className="input" value={selectedId} onChange={(e) => onSelect(e.target.value)}>
              {items.map(it => (
                <option key={it.id} value={it.id}>{it.slug} – {(it.titleLight || '') + ' ' + (it.titleAccent || '')}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Slug</label>
            <input className="input" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Sıra (orderIndex)</label>
            <input className="input" type="number" value={orderIndex} onChange={(e) => setOrderIndex(parseInt(e.target.value || '0', 10))} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Başlık (Light)</label>
            <input className="input" value={titleLight} onChange={(e) => setTitleLight(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Başlık (Accent)</label>
            <input className="input" value={titleAccent} onChange={(e) => setTitleAccent(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Açıklama</label>
            <textarea className="input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Harita Kaynağı (mapSrc)</label>
            <textarea className="input" rows={4} style={{ width: '100%' }} value={mapSrc} onChange={(e) => setMapSrc(e.target.value)} placeholder="https://... veya embed kodu" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="act" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label htmlFor="act">Aktif</label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onSave} disabled={saving || !selectedId} className="btn btn-primary">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
        </div>

        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>

      {toast.show && (
        <div role="status" aria-live="polite" style={{ position: 'fixed', right: 16, bottom: 16, background: '#16a34a', color: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 10px 20px rgba(0,0,0,0.15)' }}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default VisitItemsYonetim;
